import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import Slider from "src/components/slider";
import userRoleService from "src/services/userRoles.service.js";
import { addAssetsToCollectionOnServer, addAssetsToSectionOnServer, createCollection, invalidateCollectionCreationState } from "../../../../redux/actions/collections";
import {
  invalidateAddAssetCollections,
} from "src/redux/actions/assets";
import { animated, useTransition } from "@react-spring/web";
import { Input, SearchBar } from "../../../../components/input";
import Collapse from "../animation/collapse";
import { Collection, CollectionAsset, CollectionSection } from "src/types/collections";

import _ from "lodash";
import cx from "classnames";
import styles from "./addCollection.module.scss";
import Button from "src/components/button";
import { Asset } from "src/types/assets";
import { USER_DEFINED } from "src/constants/collections";
import { invalidateCollectionsSearchResults, searchCollections } from "src/redux/actions/search";
import InfiniteScroll from "src/components/infiniteScroll";
import { SelectEntityContext } from "src/components/selectEntity ";
import { CollectionPlaceholder, CollectionsBrowser, EntityBrowser } from "src/components/selectEntity /browser";
import { FileService } from "src/services/file.service";
import { useAssetAdoption } from "src/redux/actions/adoption/asset";
import { toast } from "react-toastify";
import Toast from "src/components/toast";

interface CustomSuccessContentProps {
  assetCount: number;
}

const CustomSuccessContent: React.FC<CustomSuccessContentProps> = ({ assetCount }) => {
  return (
    <p className={styles.successResponse}>
      {`${assetCount > 1 ? "Assets" : "Asset"} added to collection(s)`} successfully
    </p>
  );
}

interface AddToCollectionProps {
  show: boolean;
  assets: Asset[];
  onClose: () => void;
  sourceType: string
}

const AddToCollection: React.FC<AddToCollectionProps> = ({
  show,
  assets,
  onClose,
  sourceType,
}) => {

  const { trackAddToCollectionScreenView, trackCollectionSelected, trackAssetAddToCollection } = useAssetAdoption();

  const dispatch = useDispatch();
  const [expandCollection, setExpandCollection] = useState(false);
  const [selectedEntities, setSelectedEntities] = useState<{
    id: string,
    name: string,
    type: string,
    collection: string,
    entity: Collection | CollectionSection | null
  }[]>([]);

  const [collectionName, setCollectionName] = useState("");

  const [query, setQuery] = React.useState<string | null>('');

  const creating = useSelector((state: any) =>
    _.get(state, "collections.creation.creating"),
  );

  const createdCollection = useSelector((state) => {
    const finished = _.get(state, `collections.creation.created`) || false;
    if (finished) {
      return _.get(state, `collections.creation.item`) as unknown as Collection;
    }
    return null;
  });

  const adding = useSelector((state: any) => {
    return selectedEntities.some(selectedEntity => {
      const entityType = _.get(selectedEntity, 'entity.entityType');
      let collection = selectedEntity.id;
      
      if (entityType === 'collection_section') {
        collection = selectedEntity.collection;
      }
      
      return _.get(state, `asset.collections.${collection}.adding`, false);
    });
  });

  const added = useSelector((state: any) => {
    return selectedEntities.some(selectedEntity => {
      const entityType = _.get(selectedEntity, 'entity.entityType');
      let collection = selectedEntity.id;
      
      if (entityType === 'collection_section') {
        collection = selectedEntity.collection;
      }
      
      return _.get(state, `asset.collections.${collection}.added`, false);
    });
  });

  const handleQueryChange = (value) => {
    setQuery(value);
  }

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleCreateCollection = () => {
    if (collectionName.trim().length === 0 || creating) {
      return;
    }
    dispatch(createCollection({ name: collectionName }));
  };

  const handleSelection = (entity: any) => {
    let fileTypes: string[] = [];
    fileTypes = assets.map((item) => {
      return FileService.getFileType(item.metadata);
    });

    if (entity.entityType === "collection_section") {
      trackCollectionSelected({ assetType: fileTypes, collectionName: entity.collectionName, sectionName: entity.name });

      setSelectedEntities(prevSelectedEntities => {
        const selected = prevSelectedEntities.some(item => item.id === entity.id);
        
        if (selected) {
  
          return prevSelectedEntities.filter(item => item.id !== entity.id);
        } else {
  
          return [
            ...prevSelectedEntities,
            { 
              id: entity.id, 
              name: entity.name, 
              type: entity.entityType, 
              collection: entity.collection, 
              entity: entity 
            }
          ];
        }
      });
    }

    if (entity.entityType === "collection") {
      trackCollectionSelected({ assetType: fileTypes, collectionName: entity.name, sectionName: "", });

      setSelectedEntities(prevSelectedEntities => {
        const selected = prevSelectedEntities.some(item => item.id === entity.id);
        
        if (selected) {
  
          return prevSelectedEntities.filter(item => item.id !== entity.id);
        } else {
  
          return [
            ...prevSelectedEntities,
            { 
              id: entity.id, 
              name: entity.name, 
              type: entity.entityType, 
              collection: "", 
              entity: entity 
            }
          ];
        }
      });
    }
  };

  const handleAddAssets = async () => {
    const assetItems = assets.map((asset) => {
      return {
        id: asset.id,
        asset: asset,
      } as CollectionAsset;
    });

    if(_.size(selectedEntities) === 0){
      return;
    }

    selectedEntities.map((selectedEntity) => {
      if(selectedEntity.type === "collection_section"){

        dispatch(
        addAssetsToSectionOnServer({
          collection: selectedEntity.collection,
          sectionId: selectedEntity.id,
          assets: assetItems,
          addAt: "end",
        })
      );

      assets.forEach((asset) => {
        trackAssetAddToCollection({
          entity: selectedEntity.entity as CollectionSection,
          asset: asset
        });
      });

      } else if(selectedEntity.type === "collection") {
        dispatch(
          addAssetsToCollectionOnServer({
            collection: selectedEntity.id,
            assets: assetItems,
            addAt: "end",
          }),
        );

        if (selectedEntity.entity) {
          assets.forEach((asset) => {
            trackAssetAddToCollection({
              entity: selectedEntity.entity as Collection,
              asset: asset
            });
          });
        }
      }
    })
  };

  const handleOnClick = (actionType: string) => {
    if (actionType === "create") {
      handleCreateCollection();
    } else {
      handleAddAssets();
    }
  }

  const getContent = (actionType: string) => {
    if (actionType === "create") {
      return creating ? "Creating..." : "Create";
    } else {
      return adding ? "Adding..." : "Add";
    }
  }

  const handleClose = () => {
    dispatch(invalidateAddAssetCollections())
    dispatch(invalidateCollectionCreationState());
    onClose();
  }

  useEffect(() => {
    trackAddToCollectionScreenView();
  }, []);

  useEffect(() => {
    if (createdCollection && createdCollection.id) {

      const updatedEntities = [{ id: createdCollection.id, name: createdCollection.name || "", type: "collection", collection: "", entity: createCollection }];
      setSelectedEntities(updatedEntities);

      const assetItems = assets.map((asset) => {
        return {
          id: asset.id,
          asset: asset,
        } as CollectionAsset;
      });

      dispatch(
        addAssetsToCollectionOnServer({
          collection: createdCollection.id,
          assets: assetItems,
          addAt: "end",
        }),
      );
    }
  }, [createdCollection]);

  useEffect(() => {
    if (added) {
      handleClose();
      toast(
        <Toast.Sucess 
          customContent={
            <CustomSuccessContent 
              assetCount={assets?.length ?? 0}
            />
          }
          message=''
        />, 
        {
        autoClose: 30000
      })
    }
  }, [added]);

  let disabled = true;

  if (expandCollection) {
    disabled = creating || collectionName.trim().length === 0;
  }

  if (!expandCollection) {
    disabled = _.size(selectedEntities) === 0;
  }

  const browserTransition = useTransition(!query, {
    initial: { opacity: 1 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const searchTransition = useTransition(query, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  return (
    <Slider.Dialog width={"380"} show={show} onClose={() => handleClose()}>
      <Slider.Header>
        <div className={styles.content}>
          <div className={styles.collections}>
            <h1 className={styles.heading}>add to collection</h1>
            <p className={styles.subheading}>
              personalized content storyboards
            </p>
          </div>
          {userRoleService.canCreateCollection() && (
            <div
              className={styles.createCollection}
              onClick={() => setExpandCollection(!expandCollection)}
            >
              {!expandCollection && (
                <>
                  <i className={cx("paperflite-add", styles.addIcon)} />
                  <span className={styles.createText}>
                    Create New Collection
                  </span>
                </>
              )}

              {expandCollection && (
                <>
                  <i className={cx("paperflite-left-arrow", styles.backIcon)} />
                  <span className={styles.backText}>
                    Back to all Collection
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </Slider.Header>
      <Slider.Body>
        <Collapse isOpen={expandCollection}>
          <div className={styles.collectionForm}>
            <form onSubmit={handleSubmit}>
              <Input
                autoFocus
                className={styles.input}
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                placeholder="Name your collection..."
              />
            </form>
            {_.size(assets) == 1 && (
              <p className={styles.collectionFormDescription}>
                Almost done! Create this Collection by adding your first asset{" "}
                <span className={styles.highlightText}>
                  &quot;{assets[0].name}&quot;
                </span>
                <br />
                <br />
              </p>
            )}
          </div>
        </Collapse>
        {!expandCollection && (
          <div className={styles.searchBar}>
            <SearchBar query={query} onSearch={handleQueryChange} />
          </div>
        )}
        <animated.div>
          <div
            className={cx(styles.collectionBrowser, {
              [styles.hide]: expandCollection,
            })}
          >
            <SelectEntityContext.Provider value={{  selectedEntity: null, selectedEntities  }}>
              {browserTransition((style, item) => item && (
                <CollectionsBrowser
                  width={340}
                  action={(entity) => {
                    if (entity.entityType === "asset") {
                      return (
                        <button className={styles.action}>
                          <i className={cx(styles.icon, "paperflite-add")}></i>
                        </button>
                      );
                    }
                    return false;
                  }}
                  onSelection={(entity) => handleSelection(entity)}
                />
              ))}
              {searchTransition((style, item) => item && <SearchResults style={style} width={340} query={query} onSelection={handleSelection} />)}
            </SelectEntityContext.Provider>
          </div>
        </animated.div>
      </Slider.Body>
      <Slider.Actions>
        <div className={styles.actionContainer}>
          <div className={styles.buttonContainer}>
            <Button
              icon="paperflite-tick"
              variant="primary"
              onClick={() => handleOnClick(expandCollection ? "create" : "add")}
              disabled={adding || disabled}
              text={getContent(expandCollection ? "create" : "add")}
            />
            <Button disabled={adding} onClick={() => handleClose()} text="Cancel" />
          </div>
        </div>
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface SearchResultsProps {
  style: any;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({ style, width, query, onSelection }) => {

  const handleSelection = (entity) => {
    onSelection(entity);
  }

  return (
    <animated.div style={style} className={cx(styles.searchResults, styles.stack)}>
      <div className={cx(styles.stacks)}>
        <CollectionSearchResult query={query} width={width}
          action={entity => {
            return false;
          }}
          onSelection={handleSelection}
        />
      </div>
    </animated.div>
  );
}
interface CollectionSearchResultProps {
  className?: string;
  width: number;
  query?: string | null;
  // eslint-disable-next-line no-unused-vars
  action?: (entity: any) => React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  onSelection: (entity: any) => void;
}

const CollectionSearchResult: React.FC<CollectionSearchResultProps> = ({ className, query, width, action, onSelection }) => {
  const dispatch = useDispatch();

  const { loading, collections, nextPage, hasMoreItems } = useSelector((state: any) => {
    const collectionState = _.get(state, 'search.collections');
    const loading = _.get(collectionState, 'loading');
    const pagination = _.get(collectionState, 'pagination');
    const total = _.get(pagination, "totalPages", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    const collections = (_.get(collectionState, 'items') || []).filter((item: any) => {
      return item.type === USER_DEFINED && item.canEdit;
    });

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      loading: loading,
      collections: collections,
      nextPage: nextPage,
      hasMoreItems: hasMoreItems
    };
  }, _.isEqual);

  useEffect(() => {
    searchCollectionForPage(0);

    return () => {
      dispatch(invalidateCollectionsSearchResults());
    }
  }, []);

  const searchCollectionForPage = (page) => {
    if (query) {
      const request = {
        query: query,
        list: undefined,
        page: page,
        limit: 20,
        highlight: false,
      };

      dispatch(searchCollections(request));
    }
  }

  const handleLoadMoreCollections = () => {
    if (nextPage) {
      searchCollectionForPage(nextPage);
    }
  }

  return (
    <div className={cx(styles.container, styles.stacks)}>
      <InfiniteScroll className={styles.scroll}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={handleLoadMoreCollections}
        loader={() => {
          if (_.size(collections) === 0) {
            return (<>
              <CollectionPlaceholder width={'50%'} />
              <CollectionPlaceholder width={'70%'} />
              <CollectionPlaceholder width={'60%'} />
              <CollectionPlaceholder width={'50%'} />
            </>)
          } else {
            return <CollectionPlaceholder width={'70%'} />
          }
        }}
      >
      {_.size(collections) === 0 && !loading && (
          <span className={styles.noRecordsFound}>No results found</span>
        )}
        <EntityBrowser className={className}
          width={width}
          items={collections}
          action={action}
          onSelection={onSelection}
        />
      </InfiniteScroll>
    </div>
  );
}


export default AddToCollection;
